import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useRestaurant } from "./RestaurantContext";
import { getRemoteEnv } from "../config/remoteEnv";
import TimeService from "../service/TimeService";
import ToastService from "../service/ToastService";
import { useStoreHours, activePause, describePause, upcomingClosureNotice, DEFAULT_TIMEZONE, PauseDescription } from "../service/StoreHoursService";
import type { IconType } from "react-icons";
import { MdDeliveryDining, MdStorefront, MdTableRestaurant } from "react-icons/md";

// Ordering mode (fulfilment) as a URL parameter: ?mode=delivery|pickup|dinein.
//
// The URL is the source of truth — links can carry a mode, and pricing
// resolves per-fulfilment from it. sessionStorage (scoped per store) only
// RESTORES the mode when internal navigation drops the query string; a
// fresh visitor without the param gets the selection screen.
//
// The resolution is PURE and exported (resolveOrderMode + helpers) so the
// menu bake in RoutePageRoutes computes the exact same mode in the exact
// same render — prices can never be baked for a different mode than the
// one the UI believes is active.

export type OrderMode = "delivery" | "pickup" | "dinein";

export const ORDER_MODES: { id: OrderMode; label: string; Icon: IconType; blurb: string }[] = [
  { id: "delivery", label: "Delivery", Icon: MdDeliveryDining, blurb: "Delivered to your door" },
  { id: "pickup", label: "Pick Up", Icon: MdStorefront, blurb: "Collect from the restaurant" },
  { id: "dinein", label: "Dine In", Icon: MdTableRestaurant, blurb: "Ordering at your table" },
];

const isMode = (v: any): v is OrderMode => v === "delivery" || v === "pickup" || v === "dinein";

// When the app has to pick a mode ON THE USER'S BEHALF (all modes closed,
// or switching off a mode that just closed): pickup first, dine-in next,
// delivery LAST — browsers-of-menus are often just checking prices, and
// delivery prices are the ones most likely to carry a markup.
const MODE_PRIORITY: OrderMode[] = ["pickup", "dinein", "delivery"];
const priorityPick = (from: OrderMode[]): OrderMode | null =>
  MODE_PRIORITY.find((m) => from.includes(m)) || null;

export const modeStorageKey = (restaurantId?: string) => `dp-mode:${restaurantId || "store"}`;

export const enabledModesFor = (storeConfig: any): OrderMode[] => {
  if (!storeConfig) return [];
  return ORDER_MODES.map((m) => m.id).filter((id) => storeConfig[id]?.enabled);
};

export const orderingEnabledFor = (storeConfig: any, enabledModes: OrderMode[]): boolean =>
  ((!getRemoteEnv().menuOnly && storeConfig?.general?.onlineOrderingEnabled) ||
    window.location.hostname.includes("demo.")) &&
  enabledModes.length > 0;

/** The active mode for a given location + store — synchronous and pure.
 *  URL param → (unless the customer pressed "Change") QR table → legacy
 *  /pickup + /dinein paths → the store's remembered choice → a single
 *  enabled mode selecting itself. */
export function resolveOrderMode(opts: {
  search: string;
  pathname: string;
  storeConfig: any;
  restaurantId?: string;
}): OrderMode | null {
  const enabledModes = enabledModesFor(opts.storeConfig);
  const ready = !!opts.storeConfig;
  const params = new URLSearchParams(opts.search);
  const raw = params.get("mode");
  if (isMode(raw) && (!ready || enabledModes.includes(raw))) return raw;
  if (!ready || !orderingEnabledFor(opts.storeConfig, enabledModes)) return null;

  try {
    // "Change" was pressed — the customer explicitly wants the chooser;
    // don't re-infer over their head until they pick again.
    if (sessionStorage.getItem("dp-mode-choosing")) return null;
  } catch { /* ignore */ }
  try {
    if (sessionStorage.getItem("tableNumber") && enabledModes.includes("dinein")) return "dinein";
  } catch { /* ignore */ }
  if (opts.pathname.endsWith("/pickup") && enabledModes.includes("pickup")) return "pickup";
  if (opts.pathname.endsWith("/dinein") && enabledModes.includes("dinein")) return "dinein";
  try {
    const stored = sessionStorage.getItem(modeStorageKey(opts.restaurantId));
    if (isMode(stored) && enabledModes.includes(stored)) return stored;
  } catch { /* ignore */ }
  if (enabledModes.length === 1) return enabledModes[0];
  return null;
}

interface OrderModeContextValue {
  /** The selected mode, or null (selection screen should show). */
  mode: OrderMode | null;
  /** Modes this store has enabled (config-level, ignores the clock). */
  enabledModes: OrderMode[];
  /** Enabled modes that are also OPEN right now (per-mode timings).
   *  Falls back to enabledModes while the time check is pending or if it
   *  fails — never block ordering on a time-API hiccup; the server still
   *  validates at checkout. Refreshed every minute. */
  availableModes: OrderMode[];
  /** Per-mode schedule status: open flag + short label ("Open 24 hours",
   *  "Until 2 AM", "Closed · opens 11 AM"). Empty while unknown. */
  modeInfo: Partial<Record<OrderMode, { open: boolean; label: string }>>;
  /** Whether the ACTIVE mode is open right now (true while unknown — fail
   *  open; add-to-cart gates on this, checkout re-validates server-side). */
  modeOpen: boolean;
  /** The venue paused the website (switch or dated closure, resolved by the
   *  orders worker) — ordering is closed for EVERY mode while set. null =
   *  not paused or unknown (fail open). */
  pause: PauseDescription | null;
  /** Heads-up for the next closure that affects the website ("Closed
   *  tomorrow – Sat · Back on 25 October"), or null. */
  closureNotice: string | null;
  /** False until the store config has loaded — don't flash the selector. */
  ready: boolean;
  /** Whether this site takes orders at all (menu-only sites never select). */
  orderingEnabled: boolean;
  setMode: (m: OrderMode) => void;
  /** Back to the selection screen (cart's "Change"). */
  clearMode: () => void;
}

const NO_MODES: OrderMode[] = [];

const OrderModeContext = createContext<OrderModeContextValue | null>(null);

export const useOrderMode = () => {
  const ctx = useContext(OrderModeContext);
  if (!ctx) throw new Error("useOrderMode must be used within an OrderModeProvider");
  return ctx;
};

type Props = {
  /** The store's config doc (delivery/pickup/dinein blocks + general). */
  storeConfig: any;
  children: React.ReactNode;
};

export const OrderModeProvider = ({ storeConfig, children }: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { restaurantId } = useRestaurant();

  const storageKey = modeStorageKey(restaurantId);
  const enabledModes = useMemo(() => enabledModesFor(storeConfig), [storeConfig]);
  const ready = !!storeConfig;
  const orderingEnabled = orderingEnabledFor(storeConfig, enabledModes);

  const params = new URLSearchParams(location.search);
  const rawMode = params.get("mode");
  const urlMode: OrderMode | null =
    isMode(rawMode) && (!ready || enabledModes.includes(rawMode)) ? rawMode : null;

  // ── Opening hours per mode ──────────────────────────────────────────
  // Which enabled modes are open RIGHT NOW (same TimeService check the
  // cart uses at checkout). null = unknown (pending or time API down) —
  // consumers fall back to enabledModes so a hiccup never blocks ordering.
  const [openModes, setOpenModes] = useState<OrderMode[] | null>(null);
  const [modeInfo, setModeInfo] = useState<Partial<Record<OrderMode, { open: boolean; label: string }>>>({});
  useEffect(() => {
    if (!storeConfig || enabledModes.length === 0) return;
    let live = true;
    const check = async () => {
      try {
        // ONE server-time fetch per sweep, shared across the mode checks.
        const timeObj = await TimeService.getCurrentTimeDetails();
        if (!timeObj) return;
        const info: Partial<Record<OrderMode, { open: boolean; label: string }>> = {};
        for (const id of enabledModes) {
          info[id] = TimeService.describeSchedule(storeConfig[id]?.timings, timeObj);
        }
        if (!live) return;
        const open = enabledModes.filter((id) => info[id]?.open);
        setModeInfo((prev) => (JSON.stringify(prev) === JSON.stringify(info) ? prev : info));
        setOpenModes((prev) => (prev && prev.join() === open.join() ? prev : open));
      } catch {
        if (live) { setOpenModes(null); setModeInfo({}); } // unknown — fail open
      }
    };
    void check();
    const t = window.setInterval(check, 60_000);
    // Re-check the moment the tab comes back — a restored tab otherwise
    // shows up-to-60s-stale open/closed state right when it matters most.
    const onVisible = () => { if (document.visibilityState === "visible") void check(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onVisible);
    return () => {
      live = false;
      window.clearInterval(t);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeConfig, enabledModes.join()]);

  // ── Website pause (switch / dated closure) ─────────────────────────
  // The orders worker resolves it for "now"; we only read it. While
  // paused, every mode is treated as closed — the same paths a store
  // outside its hours takes (chip/chooser labels, add-to-cart gate, the
  // "closed while you were on it" toast). Unknown/failed fetch = not
  // paused, exactly as before this existed.
  const storeHours = useStoreHours(getRemoteEnv().appId, restaurantId, ready && orderingEnabled);
  const timezone = storeHours?.timezone || DEFAULT_TIMEZONE;
  const pauseState = activePause(storeHours);
  const pauseKey = pauseState ? JSON.stringify(pauseState) : "";
  // Tick every 30 s while paused so "Back at …" stays relative to now (and
  // an expiring pause lifts on time even between fetches).
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!pauseKey) return;
    const t = window.setInterval(() => setTick((n) => n + 1), 30_000);
    return () => window.clearInterval(t);
  }, [pauseKey]);
  const pause = useMemo(
    () => (pauseState ? describePause(pauseState, timezone) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pauseKey, timezone, tick]
  );
  const closureNotice = pause ? null : upcomingClosureNotice(storeHours?.closures, timezone);
  const paused = !!pause;

  // Open modes as everything below sees them: none while paused.
  const liveOpenModes = useMemo(() => (paused ? NO_MODES : openModes), [paused, openModes]);
  const liveModeInfo = useMemo(() => {
    if (!pause) return modeInfo;
    const info: Partial<Record<OrderMode, { open: boolean; label: string }>> = {};
    for (const id of enabledModes) info[id] = { open: false, label: pause.status };
    return info;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pause?.status, modeInfo, enabledModes.join()]);

  const availableModes = liveOpenModes ?? enabledModes;

  // Resolved SYNCHRONOUSLY so internal navigation that drops the query
  // string (sidebar links) never flashes the selection screen — the URL
  // catches up via the effect below.
  const mode: OrderMode | null = resolveOrderMode({
    search: location.search,
    pathname: location.pathname,
    storeConfig,
    restaurantId,
  });

  const applyMode = (m: OrderMode, replace: boolean) => {
    try {
      sessionStorage.setItem(storageKey, m);
      sessionStorage.removeItem("dp-mode-choosing");
    } catch { /* storage unavailable */ }
    const next = new URLSearchParams(location.search);
    next.set("mode", m);
    navigate(`${location.pathname}?${next.toString()}`, { replace });
  };

  const setMode = (m: OrderMode) => applyMode(m, true);

  const clearMode = () => {
    try {
      sessionStorage.removeItem(storageKey);
      sessionStorage.setItem("dp-mode-choosing", "1");
    } catch { /* ignore */ }
    const next = new URLSearchParams(location.search);
    next.delete("mode");
    const qs = next.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ""}`, { replace: true });
  };

  // Canonicalize: when the mode came from inference (not the URL), write
  // it into the URL so links/shares/pricing all see it there.
  useEffect(() => {
    if (!ready || !orderingEnabled || urlMode || !mode) return;
    applyMode(mode, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, orderingEnabled, urlMode, mode, location.pathname, location.search]);

  // Nothing selected → pick sensibly, but NEVER over the customer's head
  // when they pressed "Change" (closed modes are browsable, so the chooser
  // is a real choice even with one mode open):
  //  - exactly one mode OPEN → it selects itself;
  //  - EVERYTHING closed → default to pickup > dinein > delivery so a
  //    price-checker lands on the menu (not a chooser of closed options)
  //    with the least-marked-up prices. (The pure resolver already handles
  //    a single ENABLED mode.)
  useEffect(() => {
    if (!ready || !orderingEnabled || mode || !liveOpenModes) return;
    try { if (sessionStorage.getItem("dp-mode-choosing")) return; } catch { /* ignore */ }
    if (liveOpenModes.length === 1) applyMode(liveOpenModes[0], true);
    else if (liveOpenModes.length === 0) {
      const fallback = priorityPick(enabledModes);
      if (fallback) applyMode(fallback, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, orderingEnabled, mode, liveOpenModes]);

  // The ACTIVE mode is closed. Two distinct moments:
  //
  //  FIRST status sweep after load (prev === null) — a refreshed page, a
  //  deep link, or yesterday's remembered mode landed on a closed mode:
  //   - other modes open → the CHOOSER (silently): the customer should
  //     decide, with the statuses laid out;
  //   - everything closed → default to pickup > dinein > delivery for
  //     browsing (price-checkers see the least-marked-up prices).
  //
  //  LATER sweeps — it closed WHILE they were on it (transition):
  //   - another mode open → switch to the highest-priority open one +
  //     toast (menu and cart reprice automatically);
  //   - nothing open → stay put, indicator flips to Closed + toast.
  //  Someone who deliberately picked a closed mode from the chooser is
  //  never bounced (it was already closed at pick time — no transition).
  const prevOpenModesRef = React.useRef<OrderMode[] | null>(null);
  //  (A website pause counts as "everything closed"; its toast carries the
  //  venue's note and when ordering is back.)
  useEffect(() => {
    const openModes = liveOpenModes;
    const prev = prevOpenModesRef.current;
    prevOpenModesRef.current = openModes;
    if (!ready || !mode || !openModes || openModes.includes(mode)) return;
    if (!prev) {
      if (openModes.length > 0) {
        clearMode();
      } else {
        const fallback = priorityPick(enabledModes);
        if (fallback && fallback !== mode) applyMode(fallback, true);
      }
      return;
    }
    if (!prev.includes(mode)) return;
    const closedLabel = ORDER_MODES.find((m) => m.id === mode)?.label || mode;
    if (openModes.length > 0) {
      const next = priorityPick(openModes) || openModes[0];
      const nextLabel = ORDER_MODES.find((m) => m.id === next)?.label || next;
      applyMode(next, true);
      ToastService.showToast(`${closedLabel} has closed — switched to ${nextLabel}`, { variant: "warning" });
    } else if (pause) {
      ToastService.showToast(`${pause.message} You can still browse the menu.`, { variant: "warning" });
    } else {
      ToastService.showToast(`${closedLabel} ordering has closed — you can still browse the menu`, { variant: "warning" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, mode, liveOpenModes]);

  const modeOpen = !mode || liveOpenModes === null ? true : liveOpenModes.includes(mode);

  return (
    <OrderModeContext.Provider value={{ mode, enabledModes, availableModes, modeInfo: liveModeInfo, modeOpen, pause, closureNotice, ready, orderingEnabled, setMode, clearMode }}>
      {children}
    </OrderModeContext.Provider>
  );
};
